import { useCallback, useRef } from 'react'
import styled from 'styled-components'
import { ConfigChangeResult, GuiMarketSettings } from '../../../api/generated'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingClickBodyBuildToggleEvent,
  ScaniaAdobeTrackingClickPanoramaEvent,
  pushAdobeEvent,
} from '../../../utils/adobeAnalytics'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { getMarketSettingsState } from '../../../store/appSlice'
import {
  getBodyBuildToggled,
  getGuidedViewPage,
  getSeriesFrame,
  getStartupData,
  getUnavailableImageFrames,
  setBodyBuildToggled,
  setSeriesFrame,
  setShowPanoramaViewer,
} from '../../../store/sessionDataSlice'
import { TdsToggle } from '@scania/tegel-react'
import { GuidedViewPageType, ImageSeriesFrame } from '../../../store/types'
import { TdsToggleCustomEvent } from '@scania/tegel/dist/types/components'
import { getLatestConfigChangeResult } from '../../../store/truckInfoSlice'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import { Interior360Icon } from '../Interior360Icon'

interface ImageControlPanelRootProps {
  $isAllView: boolean
}

const ImageControlPanelRoot = styled.div<ImageControlPanelRootProps>`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: ${(props) => (props.$isAllView ? '4px' : '16px')};
  margin: 0px;
  margin-top: 4px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: flex;
  }
`

const FrameIconsWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  width: fit-content;
`

const ViewFrameIconWrapper = styled.div`
  cursor: pointer;
  height: 6px;
`

interface ViewFrameIconProps {
  $isSelected: boolean
}

const ViewFrameIcon = styled.div<ViewFrameIconProps>`
  height: 4px;
  width: 28px;
  border-radius: 0px;
  background-color: ${(props) =>
    props.$isSelected
      ? 'var(--tds-btn-primary-background)'
      : 'var(--tds-grey-500)'};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--tds-grey-700);
      height: 6px;
    }
  }
`

const InteriorAndBodybuildWrapper = styled.div`
  display: flex;
  margin-top: 16px;
  justify-content: center;
  align-items: center;
`

const Toggle = styled(TdsToggle)`
  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 16px;

  svg {
    height: 22px;
  }
`

function canShowBodyBuildForSeriesAndFrame(
  seriesFrame: ImageSeriesFrame | null,
): boolean {
  return (
    seriesFrame !== null &&
    seriesFrame.frameId === 2 &&
    seriesFrame.seriesId === 'EXTERIOR'
  )
}

function canShowBodyBuildForMarketAndConfig(
  marketSettings: GuiMarketSettings | null,
  latestConfigChangeResult: ConfigChangeResult | null,
): boolean {
  return (
    marketSettings !== null &&
    marketSettings.bodyBuildsEnabled &&
    latestConfigChangeResult !== null &&
    latestConfigChangeResult.canShowBodyBuild
  )
}

interface MainImageControlPanelMobileProps {
  showIcons: boolean
}

export function MainImageControlPanelMobile({
  showIcons,
}: MainImageControlPanelMobileProps): JSX.Element {
  const dispatch = useAppDispatch()
  const componentRoot = useRef<HTMLDivElement>(null)
  const t2 = useTextsV2()
  const startupData = useAppSelector(getStartupData)
  const marketSettings = useAppSelector(getMarketSettingsState)
  const unavailableImageFrames = useAppSelector(getUnavailableImageFrames)
  const onOffState = useAppSelector(getBodyBuildToggled)
  const seriesFrame = useAppSelector(getSeriesFrame)
  const latestConfigChangeResult = useAppSelector(getLatestConfigChangeResult)
  const gvPage = useAppSelector(getGuidedViewPage)

  const frameIsAvailable = (seriesId: string, frameId: number): boolean => {
    return (
      -1 ===
      unavailableImageFrames.findIndex(
        (u) => u.seriesId === seriesId && u.frameId === frameId,
      )
    )
  }

  const viewAlternatives = [
    { series: 'EXTERIOR', frame: 1, key: 1 },
    { series: 'EXTERIOR', frame: 2, key: 2 },
    { series: 'EXTERIOR', frame: 3, key: 3 },
    { series: 'INTERIOR', frame: 1, key: 4 },
    { series: 'INTERIOR', frame: 2, key: 5 },
    { series: 'INTERIOR', frame: 3, key: 6 },
    { series: 'INTERIOR', frame: 4, key: 7 },
  ].filter((v) => frameIsAvailable(v.series, v.frame))

  const handleViewFrameIconClick = (frame: number, series: string) => {
    handleViewChange({ seriesId: series, frameId: frame })
  }

  const handleViewChange = (view: ImageSeriesFrame) => {
    dispatch(setSeriesFrame(view))
  }

  const canShowBodyBuildForMarketAndConfigResult =
    canShowBodyBuildForMarketAndConfig(marketSettings, latestConfigChangeResult)

  const handleBodyBuildToggle = (
    e: TdsToggleCustomEvent<{
      toggleId: string
      checked: boolean
    }>,
  ) => {
    if (e === null) {
      return
    }
    const checkedInput = e.detail.checked
    const adobeEvent: ScaniaAdobeTrackingClickBodyBuildToggleEvent = {
      event: ScaniaAdobeEventId.BodyBuildToggleClick,
      eventInfo: {
        newState: checkedInput ? 'ON' : 'OFF',
      },
    }
    pushAdobeEvent(adobeEvent)
    dispatch(setBodyBuildToggled(checkedInput))
  }

  const handleOpenPanoramaViewer = useCallback(() => {
    dispatch(setShowPanoramaViewer(true))

    const trackingEvent: ScaniaAdobeTrackingClickPanoramaEvent = {
      event: ScaniaAdobeEventId.PanoramaClick,
    }
    pushAdobeEvent(trackingEvent)
  }, [dispatch])

  return (
    <ImageControlPanelRoot
      ref={componentRoot}
      $isAllView={gvPage === GuidedViewPageType.allView}
    >
      <FrameIconsWrapper>
        {viewAlternatives.map((view) => (
          <ViewFrameIconWrapper
            onClick={() => handleViewFrameIconClick(view.frame, view.series)}
            key={view.key}
          >
            <ViewFrameIcon
              key={view.key}
              $isSelected={
                seriesFrame?.seriesId === view.series &&
                seriesFrame.frameId === view.frame
                  ? true
                  : false
              }
            />
          </ViewFrameIconWrapper>
        ))}
      </FrameIconsWrapper>
      <InteriorAndBodybuildWrapper>
        {canShowBodyBuildForMarketAndConfigResult && (
          <Toggle
            disabled={!canShowBodyBuildForSeriesAndFrame(seriesFrame)}
            onTdsToggle={handleBodyBuildToggle}
            checked={onOffState}
            size="sm"
          />
        )}
        {canShowBodyBuildForMarketAndConfigResult && (
          <div>{t2(TextId.BODYBUILD_LABEL)}</div>
        )}
        {showIcons &&
          startupData?.panoramaImageWidth != null &&
          startupData.settings.interiorPanoramaEnabled &&
          latestConfigChangeResult?.cabUpdateActive === false && (
            <IconWrapper onClick={handleOpenPanoramaViewer}>
              <Interior360Icon />
            </IconWrapper>
          )}
      </InteriorAndBodybuildWrapper>
    </ImageControlPanelRoot>
  )
}
